// External imports
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Internal imports
import { listsService } from "../services/lists.service";
import {
  CreateListRequest,
  UpdateListRequest
} from "@/types/lists.types";

/**
 * Hook for fetching all user's lists
 */
export const useLists = () => {
  return useQuery({
    queryKey: ["lists"],
    queryFn: () => listsService.getLists(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching all featured lists from all users
 */
export const useFeaturedLists = () => {
  return useQuery({
    queryKey: ["featured-lists"],
    queryFn: () => listsService.getFeaturedLists(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching a specific list by ID
 */
export const useListById = (listId: string, enabled = true) => {
  return useQuery({
    queryKey: ["lists", listId],
    queryFn: () => listsService.getListById(listId),
    enabled: enabled && !!listId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for creating a new list
 */
export const useCreateList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateListRequest) => {
      return await listsService.createList(data);
    },
    onSuccess: (_response, variables) => {
      // Invalidate and refetch lists
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["list-stats"] });

      toast.success(`List "${variables.listName}" created successfully!`);
    },
    onError: (error: Error, variables) => {
      console.error("Failed to create list:", error);
      toast.error(`Failed to create list "${variables.listName}". Please try again.`);
    },
  });
};

/**
 * Hook for updating a list
 */
export const useUpdateList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, data }: { listId: string; data: UpdateListRequest }) => {
      return await listsService.updateList(listId, data);
    },
    onSuccess: (_response, variables) => {
      // Invalidate and refetch list queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["lists", variables.listId] });

      toast.success("List updated successfully!");
    },
    onError: (error: Error) => {
      console.error("Failed to update list:", error);
      toast.error("Failed to update list. Please try again.");
    },
  });
};

/**
 * Hook for deleting a list
 */
export const useDeleteList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (listId: string) => {
      return await listsService.deleteList(listId);
    },
    onSuccess: (_, listId) => {
      // Invalidate and refetch list queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.removeQueries({ queryKey: ["lists", listId] });
      queryClient.invalidateQueries({ queryKey: ["list-stats"] });

      toast.success("List deleted successfully!");
    },
    onError: (error: Error) => {
      console.error("Failed to delete list:", error);
      toast.error("Failed to delete list. Please try again.");
    },
  });
};

/**
 * Hook for adding a restaurant to a list
 */
export const useAddToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, restaurantId }: { listId: string; restaurantId: string }) => {
      return await listsService.addRestaurantToList(listId, { restaurantId });
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch list queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["lists", variables.listId] });
      queryClient.invalidateQueries({ queryKey: ["list-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-items"] });

      toast.success("Restaurant added to list!");
    },
    onError: (error: Error) => {
      console.error("Failed to add restaurant to list:", error);

      if (error.message.includes("already in this list")) {
        toast.error("Restaurant is already in this list");
      } else {
        toast.error("Failed to add restaurant to list. Please try again.");
      }
    },
  });
};

/**
 * Hook for removing a restaurant from a list
 */
export const useRemoveFromList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, restaurantId }: { listId: string; restaurantId: string }) => {
      return await listsService.removeRestaurantFromList(listId, { restaurantId });
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch list queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["lists", variables.listId] });
      queryClient.invalidateQueries({ queryKey: ["list-stats"] });

      toast.success("Restaurant removed from list!");
    },
    onError: (error: Error) => {
      console.error("Failed to remove restaurant from list:", error);
      toast.error("Failed to remove restaurant from list. Please try again.");
    },
  });
};

/**
 * Hook for checking if a restaurant is in a specific list
 */
export const useCheckInList = (listId: string, restaurantId: string, enabled = true) => {
  return useQuery({
    queryKey: ["lists", listId, "contains", restaurantId],
    queryFn: () => listsService.checkRestaurantInList(listId, restaurantId),
    enabled: enabled && !!listId && !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for getting all lists containing a specific restaurant
 */
export const useListsByRestaurant = (restaurantId: string, enabled = true) => {
  return useQuery({
    queryKey: ["lists", "by-restaurant", restaurantId],
    queryFn: () => listsService.getListsByRestaurant(restaurantId),
    enabled: enabled && !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching list statistics
 */
export const useListStats = () => {
  return useQuery({
    queryKey: ["list-stats"],
    queryFn: () => listsService.getListStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for fetching recent list items
 */
export const useRecentItems = (limit = 10) => {
  return useQuery({
    queryKey: ["recent-items", limit],
    queryFn: () => listsService.getRecentItems(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};